import {
  camelCase,
  extend,
  get,
  isEmpty,
  isUndefined,
  reduce,
  set,
  isEqual,
  isBoolean,
} from 'lodash';
import { toFixed } from 'accounting';
import { DealerCashRuleType, FinanceType } from './legos/constants';
import { fmtMoney, fmtPercent, fmtRebatePropName, fmtTaxes, optionalFmtMoney, optionalFmtPercent, optionalFmtTaxes } from './formatter';
import { FeeCategory } from './constants';
import { Overrides, Product } from './pen-utils';
import { getDealerCashRuleAppliedValue } from './payment-utils';
import { ChangeLog, DeskingOffer, GroupName } from './legos/deskingTypes';
import { AddOn } from './legos/pencilTypes';
import { IncentivesProgram } from './legos/carTypes';
import { isDefined } from './legos/utils/tsutils';
import { DealerCashRule } from './legos/paymentTypes';
import { Bundle } from './legos/penTypes';
import { getFullNameFromCurrentUser } from './user-utils';
import { LogDeskingOfferPropertyArgs } from './types/changeLog';

/**
 * This module contains functions to log deskingOffer changes into the property DeskingOffer.changeLog.
 * The changeLogs from the deskingOffers will eventually end up in Pencil.changeLog which contents
 * get displayed in the Notes section in Chairman and Papyrus.
 * 
 * The DeskingOffer.changeLog property is a JSON object with a structure like this:
   {
      terms: {
        apr: {
          new: '9.00%',
          old: '6.90%',
        },
        dealerCash: {
          new: '$450',
          old: '$500',
        },
        rebate: {
          new: '$1,500',
          old: '$1,000',
        },
      },
      nonTaxableFees: {
        floridaDocStamp: {
          new: '0.45%',
        },
      },
      taxableFees: {
        docFees: {
          new: '$850',
        },
        electronicFilingFee: {
          new: '$120',
        },
        titleRegistration: {
          new: '$500',
        },
      },
    }
*
* Most of the code was adapted from ./pencil-changelog.ts.
*/

/**
 * Converts data types that cause problems in the application.
 * @param value
 * @returns
 */
const sanitizeValue = (value: any) => {
  return isBoolean(value) ? value.toString() : value;
};

const updateLog = (
  changeLog: ChangeLog | null | undefined,
  groupName: string,
  propName: string,
  newValue: any,
  oldValue: any,
  changeMadeBy?: string,
) => {
  // do not log if value is unchanged
  if (isEqual(newValue, oldValue)) {
    return changeLog;
  }
  const sanitizedNewValue = sanitizeValue(newValue);
  const sanitizedOldValue = sanitizeValue(oldValue);
  const logs = changeLog ? { ...changeLog } : {};
  const existingOldValue = get(logs, [groupName, propName, 'old']);
  if (isDefined(sanitizedOldValue) && !existingOldValue) {
    set(logs, [groupName, propName, 'old'], sanitizedOldValue);
  }
  if (isDefined(sanitizedNewValue)) {
    set(logs, [groupName, propName, 'new'], sanitizedNewValue);
  }
  if (changeMadeBy) {
    set(logs, [groupName, propName, 'changeMadeBy'], changeMadeBy);
  }
  return logs as ChangeLog;
};

const updateMoneyProp = (
  deskingOffer: DeskingOffer,
  groupName: string,
  field: string,
  newValue: number,
  oldValue?: number,
  changeMadeBy?: string,
) => {
  const old = oldValue ?? get(deskingOffer, `data.${field}`);
  return updateLog(
    deskingOffer.changeLog,
    groupName,
    field,
    fmtMoney(newValue),
    optionalFmtMoney(old),
    changeMadeBy,
  );
};

const updatePercentProp = (
  deskingOffer: DeskingOffer,
  groupName: string,
  field: string,
  newValue: number,
  oldValue?: number,
  changeMadeBy?: string,
) => {
  const old = oldValue ?? get(deskingOffer, `data.${field}`);
  return updateLog(
    deskingOffer.changeLog,
    groupName,
    field,
    fmtPercent(newValue),
    optionalFmtPercent(old),
    changeMadeBy,
  );
};

export const updateApr = (
  deskingOffer: DeskingOffer,
  newValue: number,
  oldValue?: number,
  changeMadeBy?: string,
) => {
  return updatePercentProp(
    deskingOffer,
    GroupName.TERMS,
    'apr',
    newValue,
    oldValue,
    changeMadeBy,
  );
};

export const updateCashRebate = (
  deskingOffer: DeskingOffer,
  newValue: number,
  oldValue?: number,
  changeMadeBy?: string,
) => {
  return updateMoneyProp(
    deskingOffer,
    GroupName.TERMS,
    'rebate',
    newValue,
    oldValue,
    changeMadeBy,
  );
};

export const updateRebate = (
  deskingOffer: DeskingOffer,
  newValue: number,
  oldValue?: number,
  changeMadeBy?: string,
) => {
  if (deskingOffer.data?.financeType === FinanceType.CASH) {
    return updateCashRebate(deskingOffer, newValue, oldValue, changeMadeBy);
  }
  const propName = fmtRebatePropName(deskingOffer);
  const old = oldValue ?? get(deskingOffer, 'data.rebate');
  return updateLog(
    deskingOffer.changeLog,
    GroupName.TERMS,
    propName,
    fmtMoney(newValue),
    optionalFmtMoney(old),
    changeMadeBy
  );
};

export const updateDealerCashRule = (
  deskingOffer: DeskingOffer,
  newValue: number,
  oldValue?: number,
  changeMadeBy?: string,
) => {
  const old = oldValue ?? getDealerCashRuleAppliedValue(deskingOffer.data);
  return updateLog(
    deskingOffer.changeLog,
    GroupName.TERMS,
    'dealerCashAppliedToPrice',
    fmtMoney(newValue),
    fmtMoney(old),
    changeMadeBy
  );
};

export const updateDealerCash = (
  deskingOffer: DeskingOffer,
  newValue: number,
  oldValue?: number,
  changeMadeBy?: string,
) => {
  const updatedChangeLog = updateMoneyProp(
    deskingOffer,
    GroupName.TERMS,
    'dealerCash',
    newValue,
    oldValue,
    changeMadeBy,
  );

  // if the dealer cash rule that applies dealer cash to price uses percent
  // update the changeLog value for that as well
  const dealerCashRule = get(deskingOffer, 'data.dealerCashRule');
  if ((dealerCashRule as DealerCashRule | undefined)?.type === DealerCashRuleType.PRICE_OFFSET_PERCENT) {
    const newAppliedDealerCashValue = getDealerCashRuleAppliedValue({
      ...(deskingOffer.data || {}),
      dealerCash: newValue,
    });
    const updatedDeskingOffer: DeskingOffer = {
      ...deskingOffer,
      changeLog: updatedChangeLog,
    };
    return updateDealerCashRule(
      updatedDeskingOffer,
      newAppliedDealerCashValue,
      oldValue,
    );
  }
  return updatedChangeLog;
};

export const updateMonths = (
  deskingOffer: DeskingOffer,
  newValue: number,
  oldValue?: number,
  changeMadeBy?: string,
) => {
  const old = oldValue ?? get(deskingOffer, 'data.months');
  return updateLog(
    deskingOffer.changeLog,
    GroupName.TERMS,
    'months',
    `${newValue} months`,
    `${old} months`,
    changeMadeBy,
  );
};

export const updateResidual = (
  deskingOffer: DeskingOffer,
  newValue: number,
  oldValue?: number,
  changeMadeBy?: string,
) => {
  return updateMoneyProp(
    deskingOffer,
    GroupName.TERMS,
    'residual',
    newValue,
    oldValue,
    changeMadeBy
  );
};

export const updateResidualPercentage = (
  deskingOffer: DeskingOffer,
  newValue: number,
  oldValue?: number,
  changeMadeBy?: string,
) => {
  return updatePercentProp(
    deskingOffer,
    GroupName.TERMS,
    'residualPercentage',
    newValue,
    oldValue,
    changeMadeBy
  );
};

export const updateMileage = (
  deskingOffer: DeskingOffer,
  newValue: number,
  oldValue?: number,
  changeMadeBy?: string,
) => {
  const old = oldValue ?? get(deskingOffer, 'data.mileage');
  return updateLog(
    deskingOffer.changeLog,
    GroupName.TERMS,
    'mileage',
    newValue,
    old,
    changeMadeBy,
  );
};

export const updateRatePercentage = (
  deskingOffer: DeskingOffer,
  newValue: number,
  oldValue?: number,
  changeMadeBy?: string,
) => {
  return updatePercentProp(
    deskingOffer,
    GroupName.TERMS,
    'ratePercentage',
    newValue,
    oldValue,
    changeMadeBy
  );
};

export const updateRate = (
  deskingOffer: DeskingOffer,
  newValue: number,
  oldValue?: number,
  changeMadeBy?: string,
) => {
  const old = oldValue ?? get(deskingOffer, 'data.rate');
  return updateLog(
    deskingOffer.changeLog,
    GroupName.TERMS,
    'rate',
    toFixed(newValue, 8),
    old ? toFixed(old, 8) : old,
    changeMadeBy
  );
};

export const updateOnePaymentRate = (
  deskingOffer: DeskingOffer,
  newValue: number,
  oldValue?: number,
  changeMadeBy?: string,
) => {
  return updatePercentProp(
    deskingOffer,
    GroupName.TERMS,
    'onePaymentRate',
    newValue,
    oldValue,
    changeMadeBy
  );
};

export const updateDiscount = (
  deskingOffer: DeskingOffer,
  newValue: number,
  oldValue?: number,
  changeMadeBy?: string,
) => {
  return updateMoneyProp(
    deskingOffer,
    GroupName.TERMS,
    'discount',
    newValue,
    oldValue,
    changeMadeBy,
  );
};

export const updatePrice = (
  deskingOffer: DeskingOffer,
  newValue: number,
  oldValue?: number,
  changeMadeBy?: string,
) => {
  const old = oldValue ?? get(deskingOffer, 'data.price');
  return updateLog(
    deskingOffer.changeLog,
    GroupName.TERMS,
    'vehiclePrice',
    fmtMoney(newValue),
    optionalFmtMoney(old),
    changeMadeBy,
  );
};

export const updateTaxRate = (
  deskingOffer: DeskingOffer,
  newValue: number,
  oldValue?: number,
  changeMadeBy?: string,
) => {
  const old = oldValue ?? get(deskingOffer, 'data.tax');
  return updateLog(
    deskingOffer.changeLog,
    GroupName.TERMS,
    'tax',
    fmtPercent(newValue),
    optionalFmtPercent(old),
    changeMadeBy,
  );
};

interface tradeInProps {
  deskingOffer: DeskingOffer,
  field: string,
  newValue: number | boolean,
  oldValue?: number | boolean,
  changeMadeBy?: string,
}

export const updateTradeInProperty = (props: tradeInProps) => {
  return updateLog(
    props.deskingOffer.changeLog,
    GroupName.TRADE_INS,
    props.field,
    props.newValue,
    props.oldValue,
    props.changeMadeBy
  );
};

export const updateProperty = (
  deskingOffer: DeskingOffer,
  field: string,
  newValue: number | string | boolean,
  oldValue?: number | string | boolean,
  changeMadeBy?: string,
) => {
  const old = oldValue ?? get(deskingOffer, `data.${field}`);
  return updateLog(
    deskingOffer.changeLog,
    GroupName.TERMS,
    field,
    newValue,
    old,
    changeMadeBy
  );
};

/**
 * @deprecated Use updateIncentivesV2 instead
 * @param deskingOffer 
 * @param programs 
 * @param changeMadeBy 
 * @returns 
 */
export const updateIncentives = (
  deskingOffer: DeskingOffer,
  programs: IncentivesProgram[],
  changeMadeBy?: string,
) => {
  const oldValue = get(deskingOffer, 'data.programs');
  return updateLog(
    deskingOffer.changeLog,
    GroupName.INCENTIVES,
    'programs',
    programs,
    oldValue,
    changeMadeBy,
  );
};

/**
 * Updates the incentives details in a desking offer's changeLog
 *
 * @param {Object} params - The parameters for updating incentives.
 * @param {DeskingOffer} params.deskingOffer - The desking offer to update.
 * @param {IncentivesProgram[]} params.programs - The new incentive programs to apply.
 * @param {IncentivesProgram[]} [params.oldPrograms] - The old incentive programs, if available. Defaults to the current programs in the desking offer.
 * @param {string} [params.changeMadeBy] - The identifier of the person or system making the change.
 * @returns {ChangeLog} - Returns the updated change log after applying the incentives update.
 *
 */
export const updateIncentivesV2 = ({
  deskingOffer,
  programs,
  oldPrograms,
  changeMadeBy,
}: {
  deskingOffer: DeskingOffer;
  programs: IncentivesProgram[];
  oldPrograms?: IncentivesProgram[];
  changeMadeBy?: string;
}) => {
  const oldValue = oldPrograms ?? get(deskingOffer, 'data.programs');
  return updateLog(
    deskingOffer.changeLog,
    GroupName.INCENTIVES,
    'programs',
    programs,
    oldValue,
    changeMadeBy,
  );
};

export const updateTaxes = (
  deskingOffer: DeskingOffer,
  taxType: string,
  taxName: string,
  prop: string,
  newValue: number,
  oldValue?: number,
) => {
  const old =
    oldValue ?? get(deskingOffer, `data.taxBreakdown.${taxType}.${prop}`);
  return updateLog(
    deskingOffer.changeLog,
    GroupName.TERMS,
    `${taxName} ${prop}`,
    fmtTaxes(newValue, prop),
    optionalFmtTaxes(old, prop),
  );
};

const updateFeeItem = (logs: ChangeLog, groupName: string, fees: any[]) => {
  if (!isEmpty(fees)) {
    fees.forEach(item => {
      set(logs, [groupName, camelCase(item.name)], {
        new: item.value ? fmtMoney(item.value) : fmtPercent(item.apr),
      });
    });
  }
};

export const updateFees = (deskingOffer: DeskingOffer, newFees: any) => {
  const logs = deskingOffer.changeLog ? { ...deskingOffer.changeLog } : {};
  updateFeeItem(logs, GroupName.TAXABLE_FEES, newFees[FeeCategory.TAXABLE]);
  updateFeeItem(
    logs,
    GroupName.NON_TAXABLE_FEES,
    newFees[FeeCategory.NON_TAXABLE],
  );
  updateFeeItem(
    logs,
    GroupName.CAPITALIZED_COST_FEES,
    newFees[FeeCategory.CAPITALIZED_COST],
  );
  updateFeeItem(
    logs,
    GroupName.CAPITALIZED_COST_TAXABLE_FEES,
    newFees[FeeCategory.CAPITALIZED_COST_TAXABLE],
  );
  return logs;
};

export const updateAddOns = (deskingOffer: DeskingOffer, addOns: AddOn[]) => {
  const logs = deskingOffer.changeLog ? { ...deskingOffer.changeLog } : {};
  if (!isEmpty(addOns)) {
    const newAddOns = reduce(
      addOns,
      (acc: any, item) => {
        // eslint-disable-next-line no-param-reassign
        acc[camelCase(item.description)] = { new: fmtMoney(item.price) };
        return acc;
      },
      {},
    );
    set(logs, [GroupName.ADD_ONS], newAddOns);
  }
  return logs;
};

export const updateBundles = (
  deskingOffer: DeskingOffer,
  bundles: Bundle[],
) => {
  const logs = deskingOffer.changeLog ? { ...deskingOffer.changeLog } : {};
  if (!isEmpty(bundles)) {
    set(
      logs,
      ['protectionPlans', 'selectedBundles', 'new'],
      bundles.map(p => p.name).join(', '),
    );
  }
  return logs;
};

export const updateProducts = (
  deskingOffer: DeskingOffer,
  products: Product[],
) => {
  const logs = deskingOffer.changeLog ? { ...deskingOffer.changeLog } : {};
  if (!isEmpty(products)) {
    set(
      logs,
      ['protectionPlans', 'selectedProducts', 'new'],
      products.map(p => p.displayName).join(', '),
    );
  }
  return logs;
};

export const updatePriceOverrides = (
  deskingOffer: DeskingOffer,
  newOverrides: Overrides,
  allProducts: Product[],
) => {
  let logs = deskingOffer.changeLog ? { ...deskingOffer.changeLog } : {};
  const oldOverrides =
    deskingOffer.data?.protectionPlanProductPriceOverrides ?? {};
  const allOverrides = extend({}, oldOverrides, newOverrides);
  // Gets all the price changes made by the overrides
  if (Object.keys(allOverrides).length > 0) {
    Object.keys(allOverrides).forEach(id => {
      const oldValue = get(oldOverrides, [id]);
      const newValue = get(newOverrides, [id]);
      const changedProduct = allProducts.filter(p => p.id === id)[0];
      const productName = get(changedProduct, 'displayName');
      // Logs existing override price to a new override price

      if (oldValue && newValue) {
        logs =
          updateLog(
            logs,
            GroupName.PROTECTION_PLAN_OVERRIDES,
            productName,
            fmtMoney(newValue),
            fmtMoney(oldValue),
          ) ?? logs;
      }
      // Logs existing override price to not having an override anymore
      if (oldValue && isUndefined(newValue)) {
        logs =
          updateLog(
            logs,
            GroupName.PROTECTION_PLAN_OVERRIDES,
            productName,
            'default price',
            fmtMoney(oldValue),
          ) ?? logs;
      }
      // Logs non-existing override to having an override
      if (isUndefined(oldValue) && newValue) {
        logs =
          updateLog(
            logs,
            GroupName.PROTECTION_PLAN_OVERRIDES,
            productName,
            fmtMoney(newValue),
            'default price',
          ) ?? logs;
      }
    });
  }
  return logs;
};

export const updateAccessories = (
  deskingOffer: DeskingOffer,
  accessories: any,
) => {
  const logs = deskingOffer.changeLog ? { ...deskingOffer.changeLog } : {};
  if (!isEmpty(accessories)) {
    const accessoryMap = reduce(
      accessories,
      (acc, item) => ({ ...acc, [item.name]: { new: fmtMoney(item.price) } }),
      {},
    );
    set(logs, [GroupName.ACCESSORIES], accessoryMap);
  }
  return logs;
};

export const updateTradeInMileage = (
  deskingOffer: DeskingOffer,
  tradeInIndex: number,
  newValue: number,
  oldValue?: number,
) => {
  const old =
    oldValue ??
    get(deskingOffer, `data.pencilTradeIns[${tradeInIndex}].mileage`);
  return updateLog(
    deskingOffer.changeLog,
    GroupName.TRADE_INS,
    'Trade In Mileage',
    newValue,
    old,
  );
};

// update a property indexed under offers
export const updateOfferProperty = (
  deskingOffer: DeskingOffer,
  propName: string,
  newValue: any,
  oldValue?: any,
  changeMadeBy?: string,
) => {
  switch (propName) {
    case 'apr':
      return updateApr(deskingOffer, newValue, oldValue, changeMadeBy);
    case 'rebate':
      return updateRebate(deskingOffer, newValue, oldValue, changeMadeBy);
    case 'dealerCash':
      return updateDealerCash(deskingOffer, newValue, oldValue, changeMadeBy);
    case 'months':
      return updateMonths(deskingOffer, newValue, oldValue, changeMadeBy);
    case 'residual':
      return updateResidual(deskingOffer, newValue, oldValue, changeMadeBy);
    case 'residualPercentage':
      return updateResidualPercentage(deskingOffer, newValue, oldValue, changeMadeBy);
    case 'mileage':
      return updateMileage(deskingOffer, newValue, oldValue, changeMadeBy);
    case 'ratePercentage':
      return updateRatePercentage(deskingOffer, newValue, oldValue, changeMadeBy);
    case 'rate':
      return updateRate(deskingOffer, newValue, oldValue, changeMadeBy);
    case 'onePaymentRate':
      return updateOnePaymentRate(deskingOffer, newValue, oldValue, changeMadeBy);
    case 'discount':
      return updateDiscount(deskingOffer, newValue, oldValue, changeMadeBy);
    case 'price':
      return updatePrice(deskingOffer, newValue, oldValue, changeMadeBy);
    case 'tax':
      return updateTaxRate(deskingOffer, newValue, oldValue, changeMadeBy);
    case 'daysToFirstPayment':
      return updateProperty(deskingOffer, propName, newValue, oldValue, changeMadeBy);
    case 'dealerFees':
      // We don't need to pass changeMadeBy as we are not display object type change logs in notes section
      return updateFees(deskingOffer, newValue);
    case 'programs':
      return updateIncentivesV2({ deskingOffer, programs: newValue, oldPrograms: oldValue, changeMadeBy });
    case 'addOns':
      // We don't need to pass changeMadeBy as we are not display object type change logs in notes section
      return updateAddOns(deskingOffer, newValue);
    case 'selectedBundles':
      // We don't need to pass changeMadeBy as we are not display object type change logs in notes section
      return updateBundles(deskingOffer, newValue);
    case 'selectedProducts':
      // We don't need to pass changeMadeBy as we are not display object type change logs in notes section
      return updateProducts(deskingOffer, newValue);
    case 'selectedAccessories':
      // We don't need to pass changeMadeBy as we are not display object type change logs in notes section
      return updateAccessories(deskingOffer, newValue);
    case 'dealerCashAppliedToPrice':
      return updateDealerCashRule(deskingOffer, newValue, oldValue, changeMadeBy);
    case 'dealerCashRule': // dealer cash rule should not be present in the changelog - ignore changes by returning the changelog as is
      return deskingOffer.changeLog;
    case 'tradeInAllowance':
    case 'tradeInTaxCredit':
    case 'tradeACV':
    case 'payOff':
      return updateTradeInProperty({deskingOffer, field: propName, newValue, oldValue, changeMadeBy});
    default:
      return updateProperty(deskingOffer, propName, newValue, oldValue, changeMadeBy);
  }
};


/**
 * Logs a property change to a desking offer's changeLog.
 *
 * @param {Object} params - The parameters for logging the property change.
 * @param {DeskingOffer} params.offer - The desking offer object.
 * @param {string} params.propName - The name of the property being changed.
 * @param {unknown} params.newValue - The new value of the property.
 * @param {unknown} [params.oldValue] - The old value of the property (optional).
 * @param {Partial<User>} [params.currentUser] - The current user making the change (optional).
 * @returns {void}
 */    
export const logDeskingOfferProperty = ({
  offer,
  propName,
  newValue,
  oldValue,
  currentUser,
}: LogDeskingOfferPropertyArgs): void => {
  const changeMadeBy = getFullNameFromCurrentUser(currentUser);

  const newChangeLog = updateOfferProperty(
    offer,
    propName,
    newValue,
    oldValue,
    changeMadeBy,
  );
  set(offer, 'changeLog', newChangeLog);
};
